(ns app.front.view.group.admin.escrow.accounts
  (:require [app.domain.business.model :as business-model]
            [app.domain.fee.escrow-model :as escrow-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.ui.updated :as ui-updated]
            [app.front.util :as futil]
            [app.front.validate :as validate]
            [app.front.view.business.information :as business-info-form]
            [app.front.view.business.search :as business-search]
            [app.front.view.business.subs :as business-subs]
            [app.front.view.escrow.files :as escrow-files]
            [app.front.view.escrow.transactions :as escrow-transactions]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.escrow.counter-config-editor :as escrow-counter-config-editor]
            [app.front.view.group.admin.escrow.groups :as escrow-groups]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.permission.resolver :as permission-resolver]
            [core.user.model :as user-model]
            [core.util.schemas :as schemas]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(rf/reg-event-fx ::fetch-accounts
  (rf/path ::state)
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :fee.escrow/get-accounts-and-settings
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-accounts.success]}
     :db           (assoc db :loading? true)}))

(rf/reg-event-fx ::fetch-accounts.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status {:keys [entities permissions]}]]
    {:db (-> db
             (assoc :loading? false
                    :permissions (set permissions)))
     :fx [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]]]}))

(rf/reg-event-fx ::fetch-account
  (rf/path ::state)
  (fn [{:keys [db]} [_ group-id account-id]]
    {:comm/command {:command    :fee.escrow/get-account-details-for-admin
                    :method     :get
                    :data       (util/mmap account-id group-id)
                    :on-success [::fetch-account.success account-id]}
     :db           (update db :loading-accounts (fnil conj #{}) account-id)}))

(rf/reg-event-fx ::fetch-account.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ account-id status {:keys [entities]}]]
    (when (= status 200)
      {:db (-> db
               (update :loading-accounts disj account-id))
       :fx [[:dispatch [::entities/override-entity-db status (du/entities->entity-db entities)]]]})))

(rf/reg-sub ::balance
  (fn [[_ account-id]]
    (rf/subscribe [::entities/entity account-id]))
  :-> :escrow-account/balance)

(rf/reg-sub ::balance-str
  (fn [[_ account-id]]
    [(rf/subscribe [::balance account-id])
     (rf/subscribe [::gas/currency])])
  (fn [[balance currency] _]
    (when (and balance currency)
      (futil/money-string-whole-units balance currency))))

(rf/reg-sub ::loading-accounts
  :<- [::state]
  :-> :loading-accounts)

(rf/reg-sub ::loading-account?
  :<- [::loading-accounts]
  (fn [loading-accounts [_ account-id]]
    (contains? loading-accounts account-id)))

(rf/reg-event-db ::set-filter-text
  (rf/path ::state)
  (fn [state [_ value]]
    (assoc state :filter-text value)))

(rf/reg-sub ::filter-text
  :<- [::state]
  :-> :filter-text)

(rf/reg-sub ::name+number+accounts
  (fn [[_ group-id]]
    [(rf/subscribe [::entities/entities {:entity/parent group-id
                                         :entity/type   :entity.type/escrow-account}])
     (rf/subscribe [:language])])
  (fn [[accounts language] _]
    (->> accounts
         (map (juxt #(escrow-model/account-owner-name % language)
                    escrow-model/account-number
                    identity))
         (sort-by first))))

(rf/reg-sub ::accounts
  (fn [[_ group-id]]
    [(rf/subscribe [::name+number+accounts group-id])
     (rf/subscribe [::filter-text])])
  (fn [[accounts+names filter-text] _]
    (->> accounts+names
         (filter (fn [[name number _]]
                   (or (str/blank? filter-text)
                       (str/includes? (str/lower-case number)
                                      (str/lower-case filter-text))
                       (and name
                            (str/includes? (str/lower-case name)
                                           (str/lower-case filter-text))))))
         (sort-by first)
         (map last))))

(rf/reg-sub ::business-ids-with-account
  (fn [[_ group-id]]
    (rf/subscribe [::accounts group-id]))
  (fn [accounts _]
    (->> accounts
         (map :escrow-account/owner)
         (map :business/unique-id)
         (remove nil?)
         set)))

(rf/reg-event-fx ::open-create-modal
  (rf/path ::state)
  (fn [{state :db} [_ default-country]]
    {:db       (-> state
                   (assoc :modal-open? true)
                   (assoc-in [:new-account :escrow-account/owner-type]
                             :escrow-account.owner-type/business)
                   (assoc-in [:new-account :business/mailing-country] default-country))
     :dispatch [::business-search/init]}))

(rf/reg-event-db ::close-create-modal
  (rf/path ::state)
  (fn [state _]
    (-> state
        (assoc :modal-open? false)
        (dissoc :new-account))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::modal-open?
  :<- [::state]
  (fn [state _]
    (:modal-open? state false)))

(rf/reg-sub ::is-escrow-admin?
  :<- [::state]
  (fn [{:keys [permissions]} _]
    (contains? permissions :permission.escrow/admin)))

(rf/reg-sub ::create-account-disabled?
  :<- [::modal-open?]
  :<- [::is-escrow-admin?]
  (fn [[modal-open? is-escrow-admin?] _]
    (or modal-open? (not is-escrow-admin?))))

(rf/reg-event-db ::update-new-account
  (rf/path ::state)
  (fn [state [_ attr value]]
    (if (nil? value)
      (update state :new-account dissoc attr)
      (update state :new-account assoc attr value))))

(rf/reg-sub ::new-account
  :<- [::state]
  :-> :new-account)

(rf/reg-sub ::new-account-attr
  :<- [::new-account]
  (fn [new-account [_ attr]]
    (get new-account attr)))

(rf/reg-sub ::create-new-business?
  :<- [::state]
  (fn [state _]
    (get state :new-business? false)))

(rf/reg-event-db ::set-create-new-business
  (rf/path ::state)
  (fn [state [_ value]]
    (assoc state :new-business? value)))

(rf/reg-event-fx ::fetch-counter-config
  (rf/path ::state)
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :fee.escrow/get-escrow-account-counter-config
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-counter-config.success]}
     :db           (assoc db :loading? true)}))


(rf/reg-event-fx ::fetch-counter-config.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status {:keys [entities]}]]
    {:db (assoc db :loading? false)
     :fx [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]]]}))

(def field-error-msgs
  {:business/invite-email :validate/wrong-email-address
   :business/names        :business/name-required})

(defn new-account-data-model [account-type]
  (case account-type
    :escrow-account.owner-type/user
    {:escrow/invite-email [[schemas/Email :validate/wrong-email-address]]}

    :escrow-account.owner-type/business
    (->> escrow-model/CreateBusinessAndAccount
         (keep (fn [[k v]]
                 (when (s/required-key? k)
                   [k [[v (get field-error-msgs k :error/required-fields-missing)]]])))
         (into {}))))

(rf/reg-sub ::field-error
  :<- [::new-account]
  (fn [{:escrow-account/keys [owner-type] :as data} [_ attr]]
    (and (some? (get data attr)) ;; do not show error immeadiately
         (validate/validate-field attr data (new-account-data-model owner-type)))))

(rf/reg-sub ::new-account-valid?
  :<- [::new-account]
  :<- [::create-new-business?]
  :<- [::business-search/selected-business]
  :<- [::invite-message]
  (fn [[{:escrow-account/keys [owner-type] :as new-account-data} create-business? selected-business invite-message]
       _]
    (or (and (not create-business?) selected-business (not (util/nil-or-blank? invite-message)))
        (and (or (= :escrow-account.owner-type/business owner-type) (not (util/nil-or-blank? invite-message)))
             (empty? (validate/validate-form-strict new-account-data (new-account-data-model owner-type)))))))

(rf/reg-event-fx ::create-escrow-account
  (fn [{{{:keys [new-account
                 new-business?
                 invite-message]} ::state
         :as                      db}     :db} [_ group-id]]
    (let [{:escrow-account/keys [owner-type]} new-account]
      {:comm/command (merge (case owner-type
                              :escrow-account.owner-type/business
                              (if new-business?
                                {:command :fee.escrow/create-account-and-business
                                 :data    (merge (-> new-account
                                                     (select-keys (cons :business/invite-email
                                                                        business-model/new-business-attrs)))
                                                 (util/mmap group-id))}
                                {:command :fee.escrow/create-account-for-business
                                 :data    (util/mmap {:business/unique-id (-> db
                                                                              ::business-search/data
                                                                              ::business-search/selected-business
                                                                              :business/unique-id)}
                                                     group-id invite-message)})
                              :escrow-account.owner-type/user
                              {:command :fee.escrow/create-account-for-user
                               :data    (merge (select-keys new-account [:escrow/invite-email])
                                               (util/mmap invite-message group-id))})
                            {:on-success [::create-escrow-account.success]
                             :on-done    [::create-escrow-account.done]})
       :db           (-> db
                         (assoc-in [::state :creating-new?] true))})))

(rf/reg-event-fx ::create-escrow-account.done
  (rf/path ::state)
  (fn [{state :db} [_ status body request-id]]
    {:db (assoc state :creating-new? false)
     :fx [[:dispatch (comm/error-notification-evt status body request-id)]]}))

(rf/reg-event-fx ::create-escrow-account.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ status resp]]
    {:db (-> db
             (dissoc :new-account)
             (dissoc :invite-message)
             (assoc :creating-new? false)
             (assoc :modal-open? false))
     :fx [[:dispatch [::entities/apply-changes status resp]]]}))

(rf/reg-event-fx ::delete-escrow-account
  (rf/path ::state)
  (fn [{state :db} [_ group-id account-id]]
    {:comm/command {:command    :fee.escrow/remove-declined-account
                    :method     :post
                    :data       (util/mmap group-id account-id)
                    :on-success [::delete-escrow-account.success account-id]}
     :db           (update state :loading-accounts (fnil conj #{}) account-id)}))

(rf/reg-event-fx ::delete-escrow-account.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ account-id status resp]]
    {:db (update db :loading-accounts disj account-id)
     :fx [[:dispatch [::entities/apply-changes status resp]]]}))

(rf/reg-sub ::invite-message
  :<- [::state]
  :-> :invite-message)

(rf/reg-event-db ::set-invite-message
  (rf/path ::state)
  (fn [state [_ value]]
    (assoc state :invite-message value)))

(rf/reg-sub ::creating-new?
  :<- [::state]
  (fn [state _]
    (:creating-new? state false)))

(rf/reg-sub ::loading?
  :<- [::state]
  (fn [state _]
    (:loading? state false)))

(defn- open-modal-fn [mode]
  (fn [state [_ account-id]]
    (assoc-in state [:tx-modal account-id] (util/mmap mode))))

(rf/reg-event-db ::open-add-funds-modal
  (rf/path ::state)
  (open-modal-fn ::add-funds))

(rf/reg-event-db ::open-refund-modal
  (rf/path ::state)
  (open-modal-fn ::refund))

(rf/reg-sub ::tx-modal
  :<- [::state]
  (fn [state [_ account-id]]
    (get-in state [:tx-modal account-id])))

(defn- tx-modal-signal [[_ account-id]]
  (rf/subscribe [::tx-modal account-id]))

(rf/reg-sub ::tx-modal-open?
  tx-modal-signal
  :-> some?)

(rf/reg-event-db ::close-tx-modal
  (rf/path ::state)
  (fn [state [_ account-id]]
    (update state :tx-modal dissoc account-id)))

(rf/reg-event-db ::set-tx-amount
  (rf/path ::state)
  (fn [state [_ account-id amount]]
    (assoc-in state [:tx-modal account-id :amount] amount)))

(rf/reg-sub ::tx-amount
  tx-modal-signal
  :-> :amount)

(rf/reg-event-db ::set-tx-desc
  (rf/path ::state)
  (fn [state [_ account-id amount]]
    (assoc-in state [:tx-modal account-id :description] amount)))

(rf/reg-sub ::tx-desc
  tx-modal-signal
  :-> :description)

(rf/reg-sub ::tx-mode
  tx-modal-signal
  :-> :mode)

(defn some-str? [v]
  (and v (-> v str/trim empty? not)))

(rf/reg-sub ::tx-amount-valid?
  (fn [[_ account-id]]
    [(rf/subscribe [::tx-amount account-id])
     (rf/subscribe [::tx-mode account-id])
     (rf/subscribe [::balance account-id])])
  (fn [[amount mode balance] _]
    (or
      ;; skip validity check while no input has been given
      (not (some-str? amount))
      (let [amount (parse-double amount)]
        (and
          ;; no negative numbers for clarity
          (pos? amount)
          ;; balance after transaction must be positive - the is a separate db-fn check to enforce this
          (->> amount ((case mode ::refund - ::add-funds +) balance) (<= 0.0)))))))

(rf/reg-sub ::tx-data-valid?
  (fn [[_ account-id]]
    [(rf/subscribe [::tx-amount-valid? account-id])
     (rf/subscribe [::tx-amount account-id])
     (rf/subscribe [::tx-desc account-id])])
  (fn [[amount-valid? amount description] _]
    (and amount-valid?
         (some-str? amount)
         (some-str? description))))

(rf/reg-sub ::loading-tx?
  tx-modal-signal
  :-> :loading?)

(rf/reg-event-fx ::perform-transaction
  (rf/path ::state)
  (fn [{state :db} [_ group-id account-id amount description]]
    {:comm/command {:command    :fee.escrow/manual-transaction
                    :method     :post
                    :data       (util/mmap group-id account-id amount description)
                    :on-success [::perform-transaction.success account-id]
                    :on-done    [::perform-transaction.error account-id]}
     :db           (assoc-in state [:tx-modal account-id :loading?] true)}))

(rf/reg-event-fx ::perform-transaction.error
  (rf/path ::state)
  (fn [{:keys [db]} [_ account-id status body request-id]]
    {:db (update-in db [:tx-modal account-id] dissoc :loading?)
     :fx [(when (>= status 400) (>evt (comm/error-notification-evt status body request-id)))]}))

(rf/reg-event-fx ::perform-transaction.success
  (rf/path ::state)
  (fn [{:keys [db]} [_ account-id status resp]]
    {:db (update db :tx-modal dissoc account-id)
     :fx [[:dispatch [::entities/apply-changes status resp]]]}))

(defn input [{:keys [field tr] :as opts}]
  (let [field-error (<sub [::field-error field])
        value       (<sub [::new-account-attr field])]
    [ui/text-input (merge (util/mmap {:id         (str "new-account-" (name field))
                                      :light      true
                                      :label-text (tr [field])
                                      :on-change  #(rf/dispatch-sync [::update-new-account field (futil/tval %)])}
                                     value)
                          (when field-error
                            {:invalid      (some? field-error)
                             :invalid-text (tr [field-error])})
                          (select-keys opts [:required]))]))

(defn- create-time-col [account]
  (ui-updated/updated {:ts          (:entity/created account)
                       :user        (user-model/full-name (:entity/creator account))
                       :verb-tr-key :update-time-v2/created}))

(defn tx-widget [{:keys [tr group-id account-id]}]
  (let [amount      (<sub [::tx-amount account-id])
        description (<sub [::tx-desc account-id])
        mode        (<sub [::tx-mode account-id])]
    [ui-add/add-widget {:title    (case mode
                                    ::add-funds (tr [:escrow.management/add-funds])
                                    ::refund (tr [:escrow.management/refund]))
                        :loading? (<sub [::loading-tx? account-id])
                        :buttons  [{:text     (tr [:cancel])
                                    :on-click #(>evt [::close-tx-modal account-id])}
                                   {:text      (tr [:escrow.management/confirm])
                                    :disabled? (not (<sub [::tx-data-valid? account-id]))
                                    :on-click  (fn [] (>evt [::perform-transaction
                                                             group-id
                                                             account-id
                                                             (cond->> amount
                                                               (= mode ::refund) (str "-"))
                                                             description]))}]}
     [:div.flex.flex-col.gap-2
      [ui/number-input
       {:id           (str account-id "-tx-amount")
        :label-text   (tr [:escrow.management/amount])
        :required     true
        :invalid      (not (<sub [::tx-amount-valid? account-id]))
        :invalid-text (tr [:escrow.management/amount-invalid-help])
        :unit-label   (<sub [::gas/currency])
        :on-change    #(rf/dispatch-sync [::set-tx-amount account-id (futil/tval %)])
        :value        amount}]
      [ui/text-area
       {:id         (str account-id "-tx-description")
        :required   true
        :label-text (tr [:escrow.management/description])
        :on-change  #(rf/dispatch-sync [::set-tx-desc account-id (futil/tval %)])
        :value      description}]]]))

(def ^:private button-separator
  :div.border-t.border-solid.border-ui-gray.pt-2.mt-2.flex.justify-end.gap-2)

(defn account-details [{:keys [tr group-id]} {account-id :db/id :as account}]
  [:div.flex.flex-col.gap-2
   [:div [create-time-col account]]
   (if (<sub [::loading-account? account-id])
     (cc/skeleton-text)
     (let [has-write-access? (permission-resolver/permitted? account
                                                             (<sub [:session/user])
                                                             :permission.escrow/admin)]
       [:<>
        [:div.flex.justify-between
         (tr [:escrow.management/balance])
         [:span.font-bold (<sub [::balance-str account-id])]]
        [escrow-files/account-files-widget (util/mmap tr account-id has-write-access?)]
        [escrow-transactions/transactions
         (util/mmap {:currency (<sub [::gas/currency])
                     :title    (tr [:escrow.management/recent-transactions])}
                    account-id tr)]
        (when has-write-access?
          (case (:domain/state account)
            :escrow-account.state/active
            (if (<sub [::tx-modal-open? account-id])
              [tx-widget (util/mmap account-id group-id tr)]
              [button-separator
               [ui/button {:id       (str account-id "-add-funds")
                           :kind     :secondary
                           :on-click #(>evt [::open-add-funds-modal account-id])}
                (tr [:escrow.management/add-funds])]
               [ui/button {:id       (str account-id "-refund")
                           :kind     :secondary
                           :on-click #(>evt [::open-refund-modal account-id])}
                (tr [:escrow.management/refund])]])

            :escrow-account.state/pending
            [ui/help-2 (tr [:escrow-account.state-help/pending])]

            :escrow-account.state/declined
            [:div
             [ui/help-2 (tr [:escrow-account.state-help/declined])]
             [button-separator
              [ui/modal-confirmation-button
               {:id                  (str account-id "-delete")
                :tr                  tr
                :trigger-button-opts {:kind :primary
                                      :icon i/trash}
                :on-yes-click        #(>evt [::delete-escrow-account group-id account-id])
                :yes-button-text     (tr [:escrow.management/delete-account])
                :trigger-button-text (tr [:escrow.management/delete-account])
                :no-button-text      (tr [:cancel])}
               [:div (tr [:escrow.management/delete-account-confirm])]]]]

            ;; default, not in use
            [:div]))]))])

(defn invite-message-input [{:keys [tr]}]
  [ui/text-area
   {:id         "invite-message"
    :light      true
    :required   true
    :label-text (tr [:escrow.management/invite-message])
    :on-change  #(rf/dispatch-sync [::set-invite-message (futil/tval %)])
    :value      (<sub [::invite-message])}])

(defn new-account-dialog [{:keys [group-id tr]}]
  (let [owner-type (<sub [::new-account-attr :escrow-account/owner-type])
        lang       (<sub [:language])]
    [ui-add/add-widget {:title    (tr [:escrow.management/create-title])
                        :buttons  [{:text     (tr [:cancel])
                                    :on-click #(>evt [::close-create-modal])}
                                   {:text      (tr [:escrow.management/create])
                                    :disabled? (not (<sub [::new-account-valid?]))
                                    :on-click  #(>evt [::create-escrow-account group-id])}]
                        :loading? (<sub [::creating-new?])}
     [ui-radio/radio {:name      "business-or-individual"
                      :values    escrow-model/account-owner-types
                      :value     owner-type
                      :val->str  (fn [v] (tr [v]))
                      :on-change (fn [v] (>evt [::update-new-account :escrow-account/owner-type v]))}]
     (case owner-type
       :escrow-account.owner-type/business
       (let [create-new? (<sub [::create-new-business?])]
         [:div.mt-4
          (cc/checkbox {:id         "with-account-create-new-business-cb"
                        :label-text (tr [:escrow.management/create-new-business])
                        :checked    create-new?
                        :on-change  #(>evt [::set-create-new-business (not create-new?)])})
          (if create-new?
            [:<>
             [business-info-form/business-info-form
              (util/mmap {:validator      (<sub [::business-subs/validator-fn])
                          :light          true
                          :value-fn       (fn [field] (<sub [::new-account-attr field]))
                          :schema         escrow-model/CreateBusinessAndAccount
                          :on-change      (fn [attr v] (>evt [::update-new-account attr v]))
                          :on-change-sync (fn [attr v] (rf/dispatch-sync [::update-new-account attr v]))}
                         tr)]
             [:div.my-3 (tr [:party-modal/invite-business-owner])]
             [input (util/mmap {:field    :business/invite-email
                                :required true}
                               tr)]]

            [:div.gap-2.flex.flex-col
             (if (<sub [::business-search/selected-business])
               [business-search/selected-business-display (util/mmap tr lang)]
               [business-search/authority-existing-business-search
                (util/mmap {:hide-businesses-sub [::business-ids-with-account group-id]
                            :light               true}
                           tr lang)])
             [invite-message-input (util/mmap tr)]])])

       :escrow-account.owner-type/user
       [:div.mt-4.gap-2.flex.flex-col
        [input (util/mmap {:field    :escrow/invite-email
                           :required true}
                          tr)]
        [invite-message-input (util/mmap tr)]]
       nil)]))


(defn- manage-accounts []
  (let [tr                     (<sub [::gas/group-tr])
        group-id               (<sub [::gas/group-id])
        lang                   (<sub [:language])
        expanded-func          (partial account-details (util/mmap tr group-id))
        currency               (<sub [::gas/currency])
        counter-config-exists? (-> (<sub [::entities/entity {:counter-config/parent+type [group-id :counter-config.type/escrow-account]}])
                                   some?)
        default-country        (<sub [::front-config/country-kw])]
    [:div
     (if (<sub [::loading?])
       [section/section-skeleton]
       [section/section
        "escrow-accounts"
        (util/mmap {:heading-extra [ui/button {:id       "create-escrow-account"
                                               :disabled (or (<sub [::create-account-disabled?])
                                                             (not counter-config-exists?))
                                               :on-click #(>evt [::open-create-modal default-country])}
                                    (tr [:escrow/new-account])]}
                   tr)
        (tr [:escrow/accounts-management-subtitle])
        [:div.flex.flex-col.gap-8
         (if counter-config-exists?
           (when (<sub [::modal-open?])
             [new-account-dialog (util/mmap group-id tr)])
           [:p.bold (tr [:escrow/accounts-no-counter-config])])
         [:div.flex.mt-2.justify-end
          [:div.w-full.sm:w-1-2.lg:w-1-3
           [ui/search
            {:id                      "escrow-filter"
             :label-text              (tr [:escrow.management/search-label])
             :close-button-label-text (tr [:search/empty])
             :placeholder             (tr [:escrow.management/search-placeholder])
             :value                   (<sub [::filter-text])
             :on-change               #(>evt [::set-filter-text (futil/tval %)])
             :clear-fn                #(>evt [::set-filter-text nil])
             :hide-label              true}]]]
         [ui-table/modern-responsive-table
          (util/mmap {:cols        [[(tr [:escrow-account/id]) escrow-model/account-number]
                                    [(tr [:escrow.management/status]) #(tr [(:domain/state %)])]
                                    [(tr [:escrow.management/owner]) #(escrow-model/account-owner-name % lang)]
                                    [(tr [:escrow.management/balance])
                                     #(futil/money-string-whole-units (:escrow-account/balance %) currency)]
                                    [(tr [:escrow.management/created]) create-time-col]]
                      :items       (<sub [::accounts group-id])
                      :headerless? true
                      :get-status  (fn [{:domain/keys [state]}]
                                     (case state
                                       :escrow-account.state/active   :ok
                                       :escrow-account.state/declined :warning
                                       :task))
                      :on-expand   (fn [account-id expanded?]
                                     (when expanded?
                                       (>evt [::fetch-account group-id account-id])))
                      :link        {:make-href   (fn [{account-id :db/id}]
                                                   (href/href :view/escrow-account.transactions
                                                              {:account-id account-id}))
                                    :description (tr [:escrow/view-account])}}
                     tr expanded-func)]]])]))


(defn- escrow-configuration []
  (let [tr        (<sub [::gas/group-tr])
        group-id  (<sub [::gas/group-id])
        input-map (util/mmap tr group-id)]
    [:div
     [escrow-groups/escrow-auth-groups-selector input-map]
     [escrow-counter-config-editor/editor input-map]]))


(defn view [match]
  (let [tr (<sub [::gas/group-tr])]
    [acommon/admin-page-v2
     (util/mmap
       {:allowed-group-types #{:group.type/domain}
        :tabs                [[:escrow/accounts-management-subtitle manage-accounts]
                              [:escrow/accounts-configuration-subtitle escrow-configuration]]
        :title               (tr [:escrow/accounts-management-title])}
       match)]))


(def route
  {:name        :view/group.admin.escrow
   :own-layout? true
   :view        view
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{:keys [group-id]} :path}]
                                (>evt [::fetch-accounts group-id])
                                (>evt [::fetch-counter-config group-id]))}]})
